import { AGING_EXEMPT_STATUS_IDS, PHASE_AGING_EXEMPT_STATUS_IDS } from './constants.ts';

// Lateness escalation for the Work Queue time columns (TIME IN PHASE / TIME
// IN STEP and the SUBMITTED / APPROVED / REFI SUBMITTED milestone columns).
// The cell text itself escalates yellow → orange → red; the left nav shows
// plain counts only.

/** Which time column a value belongs to — each has its own thresholds. */
export type TimeColumn = 'phase' | 'step' | 'submitted' | 'approved' | 'refiSubmitted';

/** late = yellow, moderate = orange (moderately late), extreme = red (extremely late). */
export type LatenessTier = 'late' | 'moderate' | 'extreme';

/** Day boundaries per tier: a value of N means "N or more days". */
export interface TierThresholdDays {
    late: number;
    moderate: number;
    extreme: number;
}

/**
 * Default thresholds per column. Steps are expected to turn around faster
 * than whole phases or manufacturer round-trips, hence the tighter ladder.
 */
export const DEFAULT_THRESHOLDS: Record<TimeColumn, TierThresholdDays> = {
    step: { late: 2, moderate: 4, extreme: 6 },
    phase: { late: 7, moderate: 14, extreme: 21 },
    submitted: { late: 7, moderate: 14, extreme: 21 },
    approved: { late: 15, moderate: 21, extreme: 30 },
    refiSubmitted: { late: 7, moderate: 14, extreme: 21 }
};

/**
 * Per-step overrides, keyed by statusId. A step only needs to list the
 * columns it wants to override; everything else falls back to
 * DEFAULT_THRESHOLDS. Example:
 *
 *   'Init - 1. Records under review': {
 *     step: { late: 2, moderate: 4, extreme: 7 },
 *   },
 */
export const STEP_THRESHOLD_OVERRIDES: Record<
    string,
    Partial<Record<TimeColumn, TierThresholdDays>>
> = {};

/** Maps a WorkQueueCase milestone field to its threshold column. */
export const MILESTONE_FIELD_TO_COLUMN = {
    submittedAt: 'submitted',
    approvedAt: 'approved',
    refiSubmittedAt: 'refiSubmitted'
} as const satisfies Record<string, TimeColumn>;

export type MilestoneField = keyof typeof MILESTONE_FIELD_TO_COLUMN;

/** Tailwind classes applied to the relative-time text per tier. */
// Bright yellow, deep orange, red: apart in hue and lightness so the tiers read at a glance.
export const TIER_TEXT_CLASS: Record<LatenessTier, string> = {
    late: 'text-yellow-500 font-semibold',
    moderate: 'text-orange-600 font-semibold',
    extreme: 'text-red-600 font-semibold'
};

// Untiered time stays plain dark gray, so only late values draw the eye.
export function tierTextClass(tier: LatenessTier | null): string {
    return tier ? TIER_TEXT_CLASS[tier] : 'text-gray-700';
}

const MS_PER_DAY = 86_400_000;

/**
 * Resolves the lateness tier for an elapsed duration in a given column on a
 * given step, or null when the duration is fresh enough (or the step is
 * exempt from aging, e.g. Treatment in progress and the terminal statuses).
 * Used directly by the timeline's Journey view, whose segments carry
 * durations rather than timestamps.
 */
export function resolveLatenessTierFromMs(
    durationMs: number,
    column: TimeColumn,
    statusId: string
): LatenessTier | null {
    if (AGING_EXEMPT_STATUS_IDS.has(statusId)) return null;
    if (column === 'phase' && PHASE_AGING_EXEMPT_STATUS_IDS.has(statusId)) return null;
    const days = Math.floor(durationMs / MS_PER_DAY);
    const thresholds = STEP_THRESHOLD_OVERRIDES[statusId]?.[column] ?? DEFAULT_THRESHOLDS[column];
    if (days >= thresholds.extreme) return 'extreme';
    if (days >= thresholds.moderate) return 'moderate';
    if (days >= thresholds.late) return 'late';
    return null;
}

/** Timestamp variant: lateness of "since `iso` until now" (work-queue columns). */
export function resolveLatenessTier(
    iso: string | null,
    column: TimeColumn,
    statusId: string
): LatenessTier | null {
    if (!iso) return null;
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return null;
    return resolveLatenessTierFromMs(Date.now() - ts, column, statusId);
}
